import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BudgetGridComponent } from '../../components/budget-grid/budget-grid.component';
import { AssetManagementFileSources, AssetBudget, ReportPeriod } from '../../models';
import { Store } from '@ngxs/store';

import { AssetManagementState } from '../../state/asset-management.state';
import { combineLatestWith, take } from 'rxjs/operators';
import { BehaviorSubject, map, noop, Observable, Subject } from 'rxjs';
import { AsyncPipe } from '@angular/common';

import { TsSnackbarService } from 'tsui';
import { AssetManagementService } from '../../services/asset-management.service';
import { AssetManagementRoutingService } from '../../services/asset-management-routing.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetManagementLoaderService } from '../../services/asset-management-loader.service';

@Component({
  selector: 'budget-container',
  standalone: true,
  imports: [BudgetGridComponent, AsyncPipe],
  template: `
    <budget-grid
      [loading]="loading | async"
      [reportPeriod]="sharedReportPeriodFilter$ | async"
      [propertyBudget]="propertyBudget | async"
      (reportPeriodChanged)="reportPeriodTrigger.next($event)"
      (importBudget)="onImportBudget($event)"
    />
  `,
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetContainerComponent implements OnInit {
  readonly store = inject(Store);
  readonly snackbar = inject(TsSnackbarService);
  // TODO: Move this to state when we implement the final version of budget import
  readonly assetManagementService = inject(AssetManagementService);
  readonly assetManagementRoutingService = inject(AssetManagementRoutingService);

  readonly sharedReportPeriodFilter$ = this.store.select(AssetManagementState.sharedReportPeriodFilter);
  readonly propertyBudget: Observable<AssetBudget> = this.store.select(AssetManagementState.assetBudget);
  readonly destroyRef = inject(DestroyRef);
  readonly reportPeriodTrigger = new BehaviorSubject<ReportPeriod>(
    this.store.selectSnapshot(AssetManagementState.sharedReportPeriodFilter),
  );
  readonly loader = inject(AssetManagementLoaderService);
  private readonly queryParamsLoaded = new Subject<void>();

  readonly loading = new BehaviorSubject(false);

  get asset() {
    return this.store.selectSnapshot(AssetManagementState.asset);
  }

  ngOnInit() {
    this.reportPeriodTrigger
      .pipe(
        combineLatestWith(this.queryParamsLoaded),
        map(([reportPeriod, _]) => reportPeriod),
        this.loader.loadBudget(this.loading),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(noop);

    this.assetManagementRoutingService.retrieveQueryParamsFilters(this.reportPeriodTrigger, this.queryParamsLoaded);
  }

  onImportBudget($event: { file: File; source: AssetManagementFileSources }) {
    this.assetManagementService
      .importPropertyBudget($event.file, this.asset, $event.source)
      .pipe(take(1))
      .subscribe(() => {
        this.reportPeriodTrigger.next(this.reportPeriodTrigger.value);
      });
  }
}
